import React from 'react';
import {
  Box,
  Divider,
  FormLabel,
  Slider,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';

/**
 * Componente para renderizar una pregunta de tipo E-NPS (Net Promoter Score)
 * Escala del 0 al 10 con colores que indican detractor, pasivo o promotor
 * 
 * @param {Object} props
 * @param {number} props.questionId - ID único de la pregunta
 * @param {string} props.typeQuestion - Tipo de la pregunta
 * @param {string} props.questionName - Texto de la pregunta
 * @param {string} [props.description] - Descripción adicional de la pregunta
 * @param {number} props.index - Índice de la pregunta en el array
 * @param {Array<number>} props.unansweredQuestions - Array de índices de preguntas sin responder
 * @param {Object} props.questionRef - Ref para scroll automático
 * @param {number} props.value - Valor actual del slider (0-10)
 * @param {Function} props.onChange - Callback cuando cambia el slider
 * @param {Array<Object>} props.marks - Marcas para mostrar en el slider
 * @param {Function} props.calculateValue - Función para calcular escala (si es necesaria)
 * @param {Function} props.getSliderColor - Función que retorna el color según el valor
 * @param {boolean} [props.showDivider=true] - Si se debe mostrar el divisor al final
 */
const NPSSliderQuestion = ({
  questionId,
  typeQuestion,
  questionName,
  description,
  index,
  unansweredQuestions = [],
  questionRef,
  value,
  onChange,
  marks = [],
  calculateValue = (val) => val,
  getSliderColor,
  showDivider = true,
  hasOwnBold,
}) => {
  const hasError = unansweredQuestions.includes(index);
  const currentValue = value || 0;
  const sliderColor = getSliderColor ? getSliderColor(index) : '#1976d2';

  return (
    <>
      <FormLabel
        ref={questionRef}
        id={`${questionId}-${typeQuestion}`}
        style={{
          fontSize: '1.1',
          fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
          marginBottom: '1.1em',
          color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
        }}
      >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
      </FormLabel>

      {description && (
        <Typography
          variant="caption"
          style={{ display: 'block', fontStyle: 'italic' }}
        >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
        </Typography>
      )}

      <Box width="100%">
        <Slider
          value={currentValue}
          min={0}
          step={1}
          max={10}
          scale={calculateValue}
          onChange={(event, newValue) => onChange(event, newValue, index)}
          valueLabelDisplay="off"
          aria-labelledby="nps-slider"
          marks={marks}
          style={{ color: sliderColor }}
          sx={{
            '& .MuiSlider-track': {
              height: 10,
            },
            '& .MuiSlider-rail': {
              height: 10,
            },
            '& .MuiSlider-thumb': {
              height: 20,
              width: 20,
            },
          }}
        />
      </Box>

      {showDivider && <Divider variant="middle" />}
    </>
  );
};

export default NPSSliderQuestion;